import React, { useState } from 'react'
import UploadWavFile from './UploadWavFile'
import styled from 'styled-components'
import WaveformPlayer from './WaveformPlayer'
import { InfoTooltip } from 'ui-kit/Tooltip/InfoTooltip'
import { ProgressSpinner } from 'primereact/progressspinner'
import { bytesToHuman, isPerfDebugEnabled, perfLog, perfNow } from "utils/perfDebug";

interface AudioFile {
  file: File;
  url: string;
  id: string;
}

interface TrackUploadContentProps {
  audioFiles: AudioFile[];
  setAudioFiles: React.Dispatch<React.SetStateAction<AudioFile[]>>;
  serverAudioAvailable?: boolean;
  onLoadServerAudio?: () => void;
  replaceMode?: boolean;
  onStartReplace?: () => void;
  onCancelReplace?: () => void;
  audioPreviewLoading?: boolean;
  isTrackInfoStep?: boolean;
  startTime: string;
  setStartTime: (value: string) => void
  setFormState: React.Dispatch<React.SetStateAction<{ ai_generated: boolean; explicit_content: boolean; instrumental_music: boolean; }>>;
  formState: FormState
  loading: boolean
}
interface FormState {
  ai_generated: boolean;
  explicit_content: boolean;
  instrumental_music: boolean;
}
const TrackUploadContent: React.FC<TrackUploadContentProps> = ({
  audioFiles,
  setAudioFiles,
  serverAudioAvailable,
  onLoadServerAudio,
  replaceMode,
  onStartReplace,
  onCancelReplace,
  audioPreviewLoading,
  isTrackInfoStep,
  startTime,
  setStartTime,
  setFormState,
  formState,
  loading
}) => {

  const handleFileSelect = (file: File) => {
    const perf = isPerfDebugEnabled();
    const t0 = perf ? perfNow() : 0;
    if (perf) {
      perfLog("NewRelease.TrackUpload.selectFile", {
        name: file.name,
        type: file.type,
        size: bytesToHuman(file.size),
      });
    }
    const audio = new Audio()
    audio.src = URL.createObjectURL(file)
    audio.onloadedmetadata = () => {
      const duration = audio.duration
      const id = Math.random().toString(36).substring(2, 9)
      if (perf) {
        perfLog("NewRelease.TrackUpload.loadedmetadata", {
          ms: Math.round(perfNow() - t0),
          durationSec: Number.isFinite(duration) ? Number(duration.toFixed(3)) : duration,
        });
      }
      setAudioFiles(prev => [...prev, { file, url: audio.src, id }])
    }
  }

  return (<Container>
      {loading && (
      <Loading>
          <ProgressSpinner style={{ width: "48px", height: "48px" }} />
      </Loading>
      )}
      <>
        {audioFiles.length === 0 ? (
          <>
            {serverAudioAvailable && !replaceMode ? (
              <>
                {isTrackInfoStep && audioPreviewLoading ? (
                  <PreviewLoadingBox>
                    <PreviewLoadingRow>
                      <PlayLoader aria-label="Loading audio" />
                      <PreviewLoadingText>Loading audio preview…</PreviewLoadingText>
                    </PreviewLoadingRow>
                    <WaveformSkeleton />
                  </PreviewLoadingBox>
                ) : (
                  <ServerAudioCard>
                    <ServerAudioTopRow>
                      <ServerAudioCardText>
                        Audio is already uploaded for this track
                        <br />
                        You can load a preview (large WAV may take time)
                      </ServerAudioCardText>
                      <ServerAudioCardButton
                        type="button"
                        onClick={() => onLoadServerAudio?.()}
                        disabled={!onLoadServerAudio || !!audioPreviewLoading}
                      >
                        {audioPreviewLoading ? "Loading…" : "Load preview"}
                      </ServerAudioCardButton>
                    </ServerAudioTopRow>
                    <ReplaceRow>
                      <ReplaceHint>
                        If you want to upload a different track
                        <br />
                        To apply the new audio you must click <b>Save track</b>
                      </ReplaceHint>
                      <ReplaceButton
                        type="button"
                        onClick={() => onStartReplace?.()}
                      >
                        Upload another track
                      </ReplaceButton>
                    </ReplaceRow>
                  </ServerAudioCard>
                )}
              </>
            ) : (
          <>
            <Title>
              Upload an audio file in WAV format (stereo) with a quality of at least 44100Hz and 16 bit,
              containing 1–3 tracks, each no longer than 10 minutes *
            </Title>
            <UploadWavFile onFileSelect={handleFileSelect} />
                {serverAudioAvailable && replaceMode && (
                  <CancelReplaceRow>
                    <CancelReplaceButton type="button" onClick={() => onCancelReplace?.()}>
                      Cancel replacement
                    </CancelReplaceButton>
                  </CancelReplaceRow>
                )}
              </>
            )}
          </>
        ) : (
          audioFiles.map(({ file, url, id }) => (
            <WaveformPlayer
              key={id}
              fileUrl={url}
              fileName={file.name}
              setAudioFiles={setAudioFiles}
              startTime={startTime}
              setStartTime={setStartTime}
              onDelete={() => {
                // If this waveform is a server preview, deleting it should switch to upload mode
                // (so user can pick a new audio file) rather than showing the server-audio card again.
                if (serverAudioAvailable) onStartReplace?.();
              }}
            />
          ))
        )}
        {(!serverAudioAvailable || !!replaceMode || !!loading || (audioFiles?.length ?? 0) > 0) && (
        <FooterBox>
          <MoreBox>
            <span>More</span>
            <InfoTooltip
              name="track-more-options"
              tooltip="Use these options to mark AI‑generated content, explicit lyrics, or purely instrumental tracks. This information is required by DSPs and helps prevent takedowns or metadata conflicts."
            />
          </MoreBox>
          <Box>
            <Box>
              <InputBox>
                <Checkbox
                  type="checkbox"
                  id="ai_generated"
                  checked={formState.ai_generated}
                  onChange={() =>
                    setFormState({ ...formState, ai_generated: !formState.ai_generated })
                  }
                />
                <label htmlFor="ai_generated"> AI Generated</label>
              </InputBox>

              <InputBox>
                <Checkbox
                  type="checkbox"
                  id="explicit_content"
                  checked={formState.explicit_content}
                  onChange={() =>
                    setFormState({
                      ...formState,
                      explicit_content: !formState.explicit_content,
                      instrumental_music: formState.instrumental_music && !formState.explicit_content ? false : formState.instrumental_music,
                    })
                  }
                />
                <label htmlFor="explicit_content"> Explicit content</label>
              </InputBox>

              <InputBox>
                <Checkbox
                  type="checkbox"
                  id="instrumental_music"
                  checked={formState.instrumental_music}
                  onChange={() =>
                    setFormState({
                      ...formState,
                      instrumental_music: !formState.instrumental_music,
                      explicit_content: formState.explicit_content && !formState.instrumental_music ? false : formState.explicit_content,
                    })
                  }
                />
                <label htmlFor="instrumental_music"> Instrumental music</label>
              </InputBox>
            </Box>
          </Box>

          </FooterBox>
        )}
      </>
  </Container>
  )

}

const Container = styled.div`
  width: 100%;
  min-height: 217px;
  position: relative;
  background-color:#fff;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(2px);
  z-index: 1000;
`;

const PreviewLoadingBox = styled.div`
  width: 100%;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 16px;
  margin: 18px 0 14px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewLoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const PreviewLoadingText = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.7);
`;

const PlayLoader = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: rgba(0, 0, 0, 0.55);
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const WaveformSkeleton = styled.div`
  width: 100%;
  height: 130px;
  border-radius: 12px;
  background: #f3f3f3;
`;

const ServerAudioCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 18px 0 14px 0;
  padding: 40px 16px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const ServerAudioTopRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ServerAudioCardText = styled.div`
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 17px;
  line-height: 20px;
  color: #000000;
  opacity: 0.85;
  text-align: left;
  max-width: 520px;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const ServerAudioCardButton = styled.button`
  flex: 0 0 auto;
  border: none;
  background: #6777FF;
  color: #fff;
  border-radius: 14px;
  padding: 12px 12px;
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  min-width: 180px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReplaceRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ReplaceHint = styled.div`
  flex: 1 1 auto;
  text-align: left;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 17px;
  line-height: 20px;
  color: #000000;
  opacity: 0.85;
  max-width: 520px;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const ReplaceButton = styled.button`
  flex: 0 0 auto;
  border: none;
  background: #6777FF;
  color: #fff;
  border-radius: 14px;
  padding: 12px 12px;
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  min-width: 180px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelReplaceRow = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: center;
`;

const CancelReplaceButton = styled.button`
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  text-decoration: underline;
`;

const Title = styled.div`
  width: 82 %;
  padding: 28px 0 19px 0;
  font - family: SF Pro Display;
  font - weight: 600;
  font - size: 14px;
  line - height: 18px;
  letter - spacing: 0 %;
  `
const FooterBox = styled.div`
  padding-top:35px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  /* Mobile: "More" on top, toggles row under it */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-top: 20px;
  }

  

`
const MoreBox = styled.div`
  display: flex;
  align-items: center;
  gap:4px;

  @media (max-width: 768px) {
    width: 100%;
  }
`
const Box = styled.div`
  display: flex;
  align-items: center;
  gap:27px;

  /* Mobile: keep in a row but allow wrapping so it fits */
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
`

const InputBox = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;

  input {
    color:red;
  }

  label {
    font-family: 'SF Pro Display', sans-serif;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0;
    vertical-align: middle;
    cursor: pointer;

  }
`

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  cursor: pointer;
  position: relative;
  background-color: ${(props) => (props.checked ? "#6777FF" : "#fff")};

  &:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  `

export default TrackUploadContent;
